import { Card } from '@/components/ui/Card'
import useSetupStore from '@/store/useSetupStore'
import { Button, ButtonText } from '@/components/ui/button'
import {
  ArrowLongLeftIcon,
  ArrowLongRightIcon,
} from '@heroicons/react/16/solid'
import AboutYourBusiness from './steps/business/AboutYourBusiness'
import CreateVault from './steps/business/CreateVault'
import TeamType from './steps/TeamType'
import { useForm } from 'react-hook-form'
import useCreateTeam from '@/services/api/team/useCreateTeam.js'
import { useEffect, useState } from 'react'
import useAuthStore from '@/store/useAuthStore'
import { useNavigate } from 'react-router-dom'
import { TEAM_TYPES } from '@/data/constants/constants'
import useGetCurrentUser from '@/services/api/auth/useGetCurrentUser'
import { useQueryClient } from '@tanstack/react-query'

export default function SetupPage() {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
  const setCurrentTeamId = useAuthStore((state) => state.setCurrentTeamId)
  const queryClient = useQueryClient()
  // Only a user's first two spaces get a free trial; after that the new
  // space starts without a subscription and needs a plan straight away.
  const { data: currentUser } = useGetCurrentUser(isAuthenticated)
  const needsPlan = currentUser?.trial_eligible === false
  const teamType = useSetupStore((state) => state.teamType)
  const steps = useSetupStore((state) => state[teamType]?.steps)
  const currentStep = useSetupStore((state) => state.currentStep)
  const moveNextStep = useSetupStore((state) => state.moveNextStep)
  const movePreviousStep = useSetupStore((state) => state.movePreviousStep)
  const setTeamTypeSettings = useSetupStore(
    (state) => state.setTeamTypeSettings,
  )
  const isLastStep = useSetupStore((state) => state.isLastPage)()

  const getFormData = useSetupStore((state) => state.getData)
  const form = useForm()
  const navigate = useNavigate()

  // Space type (Business/Personal) is chosen on its own screen before the
  // per-type step sequence below even applies — steps/TeamType.jsx sets
  // useSetupStore's `teamType`, which is what `steps` above looks up.
  const [teamTypeChosen, setTeamTypeChosen] = useState(false)

  const { createTeam, createTeamStatus } = useCreateTeam({
    setError: form.setError,
    clientServerFieldMapping: {
      teamType: 'team_type',
      name: 'name',
      abn: 'abn',
    },
  })

  useEffect(() => {
    if (!isAuthenticated) navigate('/login')
  }, [navigate, isAuthenticated])

  const componentMapping = {
    AboutYourBusiness: AboutYourBusiness,
    CreateVault: CreateVault,
    TeamType: TeamType,
  }
  const firstStep = steps?.[0]
  const activeStep = teamTypeChosen
    ? currentStep ?? firstStep
    : {
        component: 'TeamType',
        title: 'Choose your space type',
        description: 'Tell us how you plan to use this space.',
      }
  const Component = componentMapping[activeStep?.component]

  // Personal has no steps of its own (no extra info needed — the backend
  // auto-names the space) — falls back to this rather than the Business
  // copy, and the body below shows a short confirmation instead of nothing.
  const isPersonal = teamType === TEAM_TYPES.PERSONAL
  const title =
    activeStep?.title ??
    (isPersonal ? 'Set up your personal space' : 'Set up your business space')
  const description =
    activeStep?.description ??
    (isPersonal
      ? "We'll create your personal space with sensible defaults — you can rename it any time."
      : 'Enter your business name to get started with ProfitFlow.')

  async function moveNext(vals) {
    if (!teamTypeChosen) {
      setTeamTypeChosen(true)
      return
    }

    setTeamTypeSettings(vals)
    if (!isLastStep) return moveNextStep()

    const data = getFormData()

    const response = await createTeam({ ...data })

    // Open the new space (the auth store persists across the reload) —
    // straight on its billing page when there's no trial to start.
    const newTeamId = response?.data?.id
    if (newTeamId) {
      // Refresh the spaces list first: PrivateRoute (still mounted on
      // /create-space) resets any current space it can't find in its cached
      // list back to the default one — before the redirect even happens.
      await queryClient.refetchQueries({ queryKey: ['teams'] })
      setCurrentTeamId(newTeamId)
    }
    window.location.href = needsPlan && newTeamId ? '/billing' : '/'
  }

  const { errors } = form.formState

  useEffect(() => {
    return
  }, [errors])
  return (
    <form
      //   onSubmit={form.handleSubmit(moveNext)}
      onSubmit={(e) => {
        e.preventDefault()
        // createTeamStatus.reset()
        form.clearErrors()
        form.handleSubmit(moveNext)()
      }}
    >
      <div className="pf-setup grid min-h-screen">
        <Card className="flex w-full max-w-[904px] flex-col place-self-center tablet:my-12 tablet:min-h-[520px]">
          <div className="grid h-full flex-1 grid-cols-1 tablet:grid-cols-[324px,1fr]">
            <div className="flex items-end bg-accent-tr-08 p-5 tablet:p-8">
              <div className="">
                <h1 className="font-bold text-txt-accent">{title}</h1>
                <p className="mt-2">{description}</p>
              </div>
            </div>
            <div className="flex flex-col justify-between p-5 pb-0 tablet:p-8 tablet:pb-0">
              <div>
                {Component ? (
                  <Component form={form} />
                ) : (
                  teamTypeChosen && (
                    <p className="text-txt-secondary">
                      {needsPlan
                        ? 'Create your space, then choose a plan for it.'
                        : 'Click Finish to create your space — you\u2019re all set.'}
                    </p>
                  )
                )}

                {needsPlan && (
                  <div
                    role="note"
                    className="mt-5 rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-950"
                  >
                    <p className="font-semibold">
                      You&apos;ve used your free trials
                    </p>
                    <p className="mt-1">
                      Free trials cover your first two spaces. This new space
                      will start without a trial — right after it&apos;s created
                      you&apos;ll go to its billing page to choose a plan.
                    </p>
                  </div>
                )}
              </div>
              <footer className="flex items-center justify-between gap-3 pb-5 pt-6 tablet:pb-8">
                <div>
                  {teamTypeChosen && (
                    <Button
                      variant="outline"
                      size="lg"
                      onClick={() =>
                        currentStep === null
                          ? setTeamTypeChosen(false)
                          : movePreviousStep()
                      }
                      type="button"
                    >
                      <ArrowLongLeftIcon className="size-5" />
                      <ButtonText>Previous</ButtonText>
                    </Button>
                  )}
                </div>
                <div>
                  <Button
                    size="lg"
                    type="submit"
                    loading={createTeamStatus.status === 'pending'}
                  >
                    <ButtonText>
                      {teamTypeChosen && isLastStep
                        ? needsPlan
                          ? 'Create & choose a plan'
                          : 'Finish'
                        : 'Next step'}
                    </ButtonText>
                    {(!teamTypeChosen || !isLastStep) && (
                      <ArrowLongRightIcon className="size-5" />
                    )}
                  </Button>
                </div>
              </footer>
            </div>
          </div>
        </Card>
      </div>
    </form>
  )
}
